import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress
} from '@mui/material';
import {
  PictureAsPdf as PdfIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import { documentService, JobDocument } from '../../services/documentService';

export interface PDFViewerIframeProps {
  document: JobDocument;
}

const PDFViewerIframe: React.FC<PDFViewerIframeProps> = ({ document }) => {
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Use form name for form PDFs, otherwise use original filename
  const displayName = document.formName || document.originalFileName;

  useEffect(() => {
    const loadPdf = async () => {
      try {
        setLoading(true);
        setError(null);

        // Create a blob URL from the downloaded PDF
        const blob = await documentService.downloadDocument(document.id);
        const url = URL.createObjectURL(blob);
        setPdfUrl(url);

        setLoading(false);
      } catch (err: any) {
        setError(`Failed to load PDF: ${err.message}`);
        setLoading(false);
      }
    };

    loadPdf();

    // Cleanup blob URL when component unmounts
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [document.id]);

  const handleDownload = async (event: React.MouseEvent) => {
    event.stopPropagation();
    try {
      await documentService.saveDocument(document);
    } catch (err) {
      alert('Download failed. Please try again.');
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          backgroundColor: 'grey.50',
          p: 4
        }}
      >
        <CircularProgress size={60} sx={{ mb: 2 }} />
        <Typography variant="h6">Loading PDF...</Typography>
        <Typography variant="body2" color="text.secondary">
          {displayName}
        </Typography>
      </Box>
    );
  }

  if (error || !pdfUrl) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          backgroundColor: 'grey.100',
          p: 4
        }}
      >
        <PdfIcon sx={{ fontSize: 80, color: 'error.main', mb: 2 }} />
        <Typography variant="h6" color="error" gutterBottom>
          {error || 'Failed to load PDF'}
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {displayName}
        </Typography>
        <Button
          variant="contained"
          startIcon={<DownloadIcon />}
          onClick={handleDownload}
          sx={{ mt: 2 }}
        >
          Download PDF
        </Button>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '90%',
        backgroundColor: 'grey.50',
        margin: 'auto'
      }}
    >
      {/* PDF Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          backgroundColor: 'white',
          borderBottom: '1px solid',
          borderColor: 'divider'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PdfIcon color="primary" />
          <Typography variant="h6" noWrap sx={{ maxWidth: '400px' }}>
            {displayName}
          </Typography>
        </Box>

        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={handleDownload}
          size="small"
        >
          Download
        </Button>
      </Box>

      {/* PDF Content */}
      <Box
        sx={{
          flex: 1,
          overflow: 'hidden',
          backgroundColor: 'white'
        }}
      >
        <iframe
          src={pdfUrl}
          style={{
            width: '100%',
            height: '100%',
            border: 'none'
          }}
          title={`PDF: ${displayName}`}
        />
      </Box>
    </Box>
  );
};

export default PDFViewerIframe;
